import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const QUERY_KEY = ['SettingsRooms']
const EMPTY_ARRAY = []

export const useSettingsRooms = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/settings/rooms`, {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        throw new Error("We couldn't load the rooms")
      }
      return res.json()
    },
  })

  return { rooms: data ?? EMPTY_ARRAY, isLoading, isError }
}

export const useAddRoom = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: addRoom, ...rest } = useMutation({
    mutationFn: async roomData => {
      const res = await fetch(`${baseURL}/v2/rest/settings/rooms`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ room: roomData }),
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not create room')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { addRoom, ...rest }
}

export const useUpdateRoom = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateRoom, ...rest } = useMutation({
    mutationFn: async ({ id, ...roomData }) => {
      const res = await fetch(`${baseURL}/v2/rest/settings/rooms/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ room: roomData }),
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not update room')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { updateRoom, ...rest }
}

export const useDeleteRoom = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: deleteRoom, ...rest } = useMutation({
    mutationFn: async id => {
      const res = await fetch(`${baseURL}/v2/rest/settings/rooms/${id}`, {
        method: 'DELETE',
        headers,
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not delete room')
      }
      return res.status === 204 ? {} : res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { deleteRoom, ...rest }
}
